// sim/capacity.ts — ECOLOGICAL CARRYING CAPACITY: the population-stability foundation.
//
// THE PROBLEM it solves: colonies used to breed to a FIXED ceiling (maxPopulation),
// boom past what their economy could feed, then crash — boom-and-bust that wrecks
// long-term progression. This layer gives every colony a DYNAMIC carrying capacity
// derived from its real economy, and governs reproduction + mortality against it so
// populations settle near what they can sustain instead of overshooting and crashing.
//
// THE FOUNDATION (read this if you're adding a population mechanic):
//   Every colony maintains three numbers — Current Population, Carrying Capacity,
//   Population Pressure (pop ÷ capacity, in permille). FUTURE SYSTEMS SHOULD READ
//   `capacityOf` / `pressureOf` / `pressureStateOf` here instead of inventing their
//   own population limits. The colony spawn loop and the AI already do (see
//   ReproductionGovernor + colonyAI.attachCapacity). Docs: docs/CAPACITY.md.
//
// CAPACITY = base
//          + ⌊foodStorage / storageDivisor⌋        (a full larder supports more brood)
//          + foodIncome   · incomeWeight           (a strong supply line sustains more)
//          + hiveNodes    · hiveNodeWeight          (founded expansion points = room)
//          + hiveChambers · hiveChamberWeight       (physical hive size, optional)
//          + territory    · territoryWeight         (claimed regions, optional)
//          + projects     · projectWeight           (infrastructure projects, optional)
//   clamped to [minCapacity, maxCapacity]. maxCapacity == colony.maxPopulation: the
//   dynamic value lives INSIDE the hard entity-allocation bound, never beyond it.
//
// RULES (pressure = pop·1000 ÷ capacity):
//   below capacity (pressure < nearBand) → INCREASED reproduction (shorter cooldown)
//   near  capacity (nearBand ≤ p < 1000) → STABLE reproduction (normal cooldown)
//   above capacity (pressure ≥ 1000)     → reproduction STOPS (the spawn gate),
//                                          + INCREASED mortality (a throttled cull)
//                                          + REDUCED efficiency (an overcrowding food tax)
//
// DETERMINISM: pure integer state, NO RNG (capacity is a function of colony state),
// fixed colony order, exact FNV hash. The cull is a QUIET despawn (no loot roll, so it
// never touches the shared RNG stream). Allocation-free per tick. OPTIONAL everywhere
// it integrates, so a world without it is byte-identical (base colony world + its tests).

import { CASTE, KIND } from '../types.ts';
import type { ColonyId, System, Tick, Caste } from '../types.ts';
import { fnvInit, fnvU32 } from '../hash.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { ColonySystem } from './colony.ts';

/** Pressure band a colony sits in (the headline stability metric). */
export const PRESSURE_STATE = {
  /** pressure < nearBand — room to grow; reproduction is boosted. */
  Growing: 0,
  /** nearBand ≤ pressure < 1000 — at a healthy hold; reproduction is normal. */
  Stable: 1,
  /** pressure ≥ 1000 — over capacity; reproduction stops, attrition + waste kick in. */
  OverCapacity: 2,
} as const;
export type PressureState = (typeof PRESSURE_STATE)[keyof typeof PRESSURE_STATE];

export interface CapacityConfig {
  /** Floor capacity every colony gets for free (a nascent colony can still grow). */
  readonly base: number;
  /** Food storage divided by this adds capacity (a stocked larder feeds more brood). */
  readonly storageDivisor: number;
  /** Capacity per unit of food income measured over one cadence. */
  readonly incomeWeight: number;
  /** Capacity per founded hive node (autonomy expansion points). */
  readonly hiveNodeWeight: number;
  /** Capacity per physical hive chamber (optional HiveSystem). */
  readonly hiveChamberWeight: number;
  /** Capacity per claimed region/territory unit (optional). */
  readonly territoryWeight: number;
  /** Capacity per completed infrastructure project (optional). */
  readonly projectWeight: number;
  /** Hard minimum capacity (never starve a young colony into a cull). */
  readonly minCapacity: number;
  /** Hard maximum capacity == colony.maxPopulation (the entity-allocation bound). */
  readonly maxCapacity: number;
  /** Ticks between recompute/attrition passes (capacity is stable between them). */
  readonly cadence: number;
  /** Pressure (permille) at/above which reproduction is "stable" (normal cooldown). */
  readonly nearBandPermille: number;
  /** Spawn-cooldown scale (permille) at pressure 0 — the fastest reproduction. */
  readonly minCooldownScale: number;
  /** Food wasted per cadence while over capacity (the reduced-efficiency penalty). */
  readonly efficiencyTax: number;
  /** Never cull a colony at or below this population (don't wipe a struggling colony). */
  readonly cullFloor: number;
}

export function defaultCapacityConfig(maxPopulation: number): CapacityConfig {
  return {
    base: 20,
    storageDivisor: 4,
    incomeWeight: 1,
    hiveNodeWeight: 8,
    hiveChamberWeight: 4,
    territoryWeight: 6,
    projectWeight: 12,
    minCapacity: 24,
    maxCapacity: maxPopulation,
    cadence: 16,
    nearBandPermille: 850,
    // Reproduction-acceleration is OFF by default (1000 = normal cooldown at every
    // pressure). Capacity is deliberately a CEILING here: it prevents OVERSHOOT
    // (gate + cull + waste above capacity) and otherwise leaves a below-capacity
    // colony reproducing at its natural, food-paced rate — byte-identical to the
    // pre-capacity world. Actively SPEEDING breeding while capacity is generous
    // (food-decoupled) over-breeds a scarce larder and HASTENS collapse — the very
    // boom-bust we're killing. The hook (minCooldownScale < 1000) stays in the API
    // for when capacity is tightly food-coupled (see docs/CAPACITY.md + BACKLOG).
    minCooldownScale: 1000,
    efficiencyTax: 3,
    cullFloor: 6,
  };
}

/** Optional per-colony structural readers (absent ⇒ that term contributes 0). */
export interface CapacityInputs {
  /** Founded hive nodes (e.g. ColonyAISystem.hiveCountAt). */
  readonly hiveNodesOf?: (cid: ColonyId) => number;
  /** Physical hive chambers (e.g. HiveSystem). */
  readonly hiveChambersOf?: (cid: ColonyId) => number;
  /** Claimed territory/regions (e.g. RivalSystem.regionsHeldBy). */
  readonly territoryOf?: (cid: ColonyId) => number;
  /** Completed infrastructure projects (e.g. ColonyProjectSystem.completedCountOf). */
  readonly projectsOf?: (cid: ColonyId) => number;
}

/**
 * THE foundation contract a population mechanic reads. The colony spawn loop takes
 * one of these (see ColonySystem.attachReproduction) to govern reproduction by
 * capacity instead of a fixed cap. `CarryingCapacitySystem` implements it.
 */
export interface ReproductionGovernor {
  /** This colony's current carrying capacity (already clamped ≤ maxPopulation). */
  capacityOf(cid: ColonyId): number;
  /** Spawn-cooldown multiplier in permille (1000 = normal; <1000 = faster). */
  cooldownScaleOf(cid: ColonyId): number;
}

// --- Pure calculations (deterministic; unit-tested headless) ------------------

/** Carrying capacity from the colony's economy, clamped to [min, max]. */
export function computeCarryingCapacity(
  income: number,
  storage: number,
  hiveNodes: number,
  hiveChambers: number,
  territory: number,
  projects: number,
  cfg: CapacityConfig,
  popCapBonus = 0,
): number {
  let cap = cfg.base
    + Math.floor((storage > 0 ? storage : 0) / cfg.storageDivisor)
    + (income > 0 ? income : 0) * cfg.incomeWeight
    + (hiveNodes > 0 ? hiveNodes : 0) * cfg.hiveNodeWeight
    + (hiveChambers > 0 ? hiveChambers : 0) * cfg.hiveChamberWeight
    + (territory > 0 ? territory : 0) * cfg.territoryWeight
    + (projects > 0 ? projects : 0) * cfg.projectWeight
    // PopCap-type projects ("rafts + shoreline runs feed more mouths") raise the
    // ceiling ON TOP of the universal per-project weight. 0 (default / unwired) ⇒
    // byte-identical to before this term existed.
    + (popCapBonus > 0 ? popCapBonus : 0);
  if (cap < cfg.minCapacity) cap = cfg.minCapacity;
  if (cap > cfg.maxCapacity) cap = cfg.maxCapacity;
  return cap;
}

/** Population pressure in permille: pop·1000 ÷ capacity (2000 = no capacity at all). */
export function pressurePermille(pop: number, capacity: number): number {
  if (capacity <= 0) return pop > 0 ? 2000 : 0;
  return Math.floor((pop * 1000) / capacity);
}

/** Pressure band (Growing / Stable / OverCapacity). */
export function pressureState(pressure: number, cfg: CapacityConfig): PressureState {
  if (pressure >= 1000) return PRESSURE_STATE.OverCapacity;
  if (pressure >= cfg.nearBandPermille) return PRESSURE_STATE.Stable;
  return PRESSURE_STATE.Growing;
}

/**
 * Spawn-cooldown scale (permille of the base cooldown) for a pressure: far below
 * capacity reproduces fastest (minCooldownScale), ramping linearly to normal (1000)
 * at the near-band and staying normal above it (the gate stops repro over capacity).
 */
export function reproductionCooldownScale(pressure: number, cfg: CapacityConfig): number {
  if (pressure >= cfg.nearBandPermille) return 1000;
  const p = pressure > 0 ? pressure : 0;
  return cfg.minCooldownScale + Math.floor(((1000 - cfg.minCooldownScale) * p) / cfg.nearBandPermille);
}

// --- The system --------------------------------------------------------------

export class CarryingCapacitySystem implements System, ReproductionGovernor {
  readonly name = 'capacity';
  readonly capacity: number;
  private readonly colonies: ColonySystem;
  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly config: CapacityConfig;
  private readonly inputs: CapacityInputs;
  /**
   * OPTIONAL per-colony PopCap project bonus (late-bound via `attachPopCapBonus`). When
   * wired, completed PopCap-type projects raise the colony's carrying-capacity ceiling on
   * top of the universal project weight. Absent ⇒ contributes 0, byte-identical to before.
   */
  private popCapBonusOf: ((cid: ColonyId) => number) | null = null;
  /**
   * OPTIONAL per-colony established-TERRITORY score (late-bound via `attachTerritory`).
   * When wired, it ADDS to the `territoryOf` input (which is unwired in the live world,
   * so no double-count) — established/integrated provinces lift the colony's ceiling.
   * Absent ⇒ contributes 0, byte-identical to before.
   */
  private territoryEstablishmentOf: ((cid: ColonyId) => number) | null = null;

  // Per-colony SoA.
  private readonly cap: Int32Array;        // current carrying capacity
  private readonly pressure: Int32Array;   // current pressure (permille)
  private readonly lastDeposited: Int32Array; // income-window watermark (food deposited)
  // Last-sampled food income per cadence (deposit delta). A DERIVED CACHE — the
  // diagnostics read-model (src/sim/diagnostics.ts) reads it via `incomeOf` to
  // explain a colony's supply line. DELIBERATELY NOT folded into `hash()`: it is a
  // pure function of state the hash already covers (depositedByAt + the cadence
  // clock), so keeping it out preserves byte-identity (the ecosystem hash is
  // unchanged from before this field existed).
  private readonly income: Int32Array;

  /** Recompute passes run (deterministic; folded into the hash). */
  recomputeTotal = 0;
  /** Over-capacity culls performed (deterministic; folded into the hash). */
  cullTotal = 0;

  constructor(
    colonies: ColonySystem,
    entities: EntitySystem,
    grid: WorldGrid,
    config: CapacityConfig,
    inputs: CapacityInputs = {},
  ) {
    this.colonies = colonies;
    this.entities = entities;
    this.grid = grid;
    this.config = config;
    this.inputs = inputs;
    this.capacity = colonies.capacity;
    // Start at the hard ceiling so colonies grow normally until the first recompute.
    this.cap = new Int32Array(this.capacity).fill(config.maxCapacity);
    this.pressure = new Int32Array(this.capacity);
    this.lastDeposited = new Int32Array(this.capacity);
    this.income = new Int32Array(this.capacity);
  }

  update(tick: Tick): void {
    if (tick % this.config.cadence !== 0) return;
    const cfg = this.config;
    const count = this.colonies.count;
    for (let cid = 0; cid < count; cid++) {
      if (!this.colonies.isAlive(cid)) continue;

      // Food income over the last cadence (per-colony deposit delta, clamped ≥ 0).
      const deposited = this.colonies.depositedByAt(cid);
      let income = deposited - (this.lastDeposited[cid] as number);
      if (income < 0) income = 0;
      this.lastDeposited[cid] = deposited;
      // Cache the sampled income for the diagnostics read-model (non-hashed).
      this.income[cid] = income;

      const cap = computeCarryingCapacity(
        income,
        this.colonies.foodAt(cid),
        this.readNodes(cid),
        this.readChambers(cid),
        this.readTerritory(cid),
        this.readProjects(cid),
        cfg,
        this.popCapBonusOf ? this.popCapBonusOf(cid) : 0,
      );
      this.cap[cid] = cap;
      const pop = this.colonies.populationAt(cid);
      this.pressure[cid] = pressurePermille(pop, cap);

      // OVER CAPACITY: reduced efficiency (overcrowding wastes food) + increased
      // mortality (a single throttled cull, never below the floor). Reproduction is
      // already gated off by the spawn loop (pop ≥ cap).
      if (pop > cap) {
        if (cfg.efficiencyTax > 0) this.colonies.stealFood(cid, cfg.efficiencyTax);
        if (pop > cfg.cullFloor) this.cullOne(cid);
      }
    }
    this.recomputeTotal++;
  }

  /** Quietly despawn one of this colony's members (prefer a Worker). No loot, no RNG. */
  private cullOne(cid: ColonyId): void {
    const e = this.entities;
    const active = e.activeView();
    const n = e.aliveCount;
    let victim = -1;
    let fallback = -1;
    for (let i = 0; i < n; i++) {
      const id = active[i] as number;
      if ((e.kind[id] as number) !== KIND.Organism) continue;
      if ((e.colony[id] as number) !== cid) continue;
      if (fallback < 0) fallback = id;
      if ((e.caste[id] as number) === CASTE.Worker) { victim = id; break; }
    }
    const id = victim >= 0 ? victim : fallback;
    if (id < 0) return;
    // Quiet attrition death: colony bookkeeping + grid removal + despawn (no loot
    // roll, so the shared RNG stream is untouched and determinism is preserved).
    this.colonies.onDeath(cid, e.caste[id] as Caste);
    this.grid.remove(id);
    e.despawn(id);
    this.cullTotal++;
  }

  private readNodes(cid: ColonyId): number {
    return this.inputs.hiveNodesOf ? this.inputs.hiveNodesOf(cid) : 0;
  }
  private readChambers(cid: ColonyId): number {
    return this.inputs.hiveChambersOf ? this.inputs.hiveChambersOf(cid) : 0;
  }
  private readTerritory(cid: ColonyId): number {
    const wired = this.inputs.territoryOf ? this.inputs.territoryOf(cid) : 0;
    const live = this.territoryEstablishmentOf ? this.territoryEstablishmentOf(cid) : 0;
    return wired + live;
  }
  private readProjects(cid: ColonyId): number {
    return this.inputs.projectsOf ? this.inputs.projectsOf(cid) : 0;
  }

  /**
   * Late-bind the PopCap project bonus (mirrors the other optional seams). Once wired,
   * completed PopCap-type projects lift a colony's capacity ceiling; not wiring it leaves
   * the capacity byte-identical.
   */
  attachPopCapBonus(source: (cid: ColonyId) => number): void {
    this.popCapBonusOf = source;
  }

  /**
   * Late-bind the established-territory score (mirrors the other optional seams). Once
   * wired, a colony's established/integrated provinces lift its capacity ceiling; not
   * wiring it leaves the capacity byte-identical.
   */
  attachTerritory(source: (cid: ColonyId) => number): void {
    this.territoryEstablishmentOf = source;
  }

  // --- ReproductionGovernor + read models (the foundation API) ---------------

  capacityOf(cid: ColonyId): number {
    return this.cap[cid] as number;
  }
  cooldownScaleOf(cid: ColonyId): number {
    return reproductionCooldownScale(this.pressure[cid] as number, this.config);
  }
  /** Population pressure (permille) — the headline stability metric. */
  pressureOf(cid: ColonyId): number {
    return this.pressure[cid] as number;
  }
  /** Pressure band (Growing / Stable / OverCapacity). */
  pressureStateOf(cid: ColonyId): PressureState {
    return pressureState(this.pressure[cid] as number, this.config);
  }
  /**
   * Last-sampled food income (deposit delta) over one cadence — a DERIVED CACHE
   * read by the diagnostics read-model (src/sim/diagnostics.ts) to explain a
   * colony's supply line. NOT folded into `hash()` (see the `income` field): it is
   * a pure function of already-hashed state, so excluding it keeps byte-identity.
   */
  incomeOf(cid: ColonyId): number {
    return this.income[cid] as number;
  }

  /** Fold per-colony capacity + pressure into a running FNV hash (determinism). */
  hash(): number {
    let h = fnvInit();
    for (let cid = 0; cid < this.colonies.count; cid++) {
      h = fnvU32(h, this.cap[cid] as number);
      h = fnvU32(h, this.pressure[cid] as number);
    }
    h = fnvU32(h, this.recomputeTotal);
    h = fnvU32(h, this.cullTotal);
    return h >>> 0;
  }
}
